import {
  VdChatInboxItem,
  VdChatMessage,
  VdChatMessagesResponse,
  VdChatSearchResponse,
  VdChatTranslation,
  VdChatUnreadCount,
  VdPresignChatAttachmentDto,
  VdPresignChatAttachmentResponse,
  VdSendChatMessageDto,
} from '../../types/deliveriesConsole';
import { ResponseType } from '../../types/shared.types';
import { api } from '../api';
import { vdOrgPath } from './base';

/*
 * One thread per driver, addressed by driverId; there is no "open
 * conversation" call and the deep link works before the first message.
 * Every active member sees every thread and they share one read state.
 */
const chatsPath = (orgId: number | string) => `${vdOrgPath(orgId)}/chats`;
const chatPath = (orgId: number | string, driverId: number | string) =>
  `${chatsPath(orgId)}/${driverId}`;

/** Every signed-up driver (applicants too), newest conversation first. */
export const vdGetChatInbox = (
  orgId: number | string,
  params: { query?: string; unreadOnly?: boolean } = {}
) => api.get<ResponseType<VdChatInboxItem[]>>(chatsPath(orgId), { params });

export const vdGetChatUnreadCount = (orgId: number | string) =>
  api.get<ResponseType<VdChatUnreadCount>>(`${chatsPath(orgId)}/unread-count`);

export const vdGetChat = (orgId: number | string, driverId: number | string) =>
  api.get<ResponseType<VdChatInboxItem>>(chatPath(orgId, driverId));

/** Newest first; pass the previous `nextBeforeId` as `beforeId` for older. */
export const vdGetChatMessages = (
  orgId: number | string,
  driverId: number | string,
  params: { beforeId?: number; limit?: number } = {}
) =>
  api.get<ResponseType<VdChatMessagesResponse>>(
    `${chatPath(orgId, driverId)}/messages`,
    { params }
  );

/** Idempotent on `clientMessageId`; sending also marks the thread read. */
export const vdSendChatMessage = (
  orgId: number | string,
  driverId: number | string,
  data: VdSendChatMessageDto
) =>
  api.post<ResponseType<VdChatMessage>>(
    `${chatPath(orgId, driverId)}/messages`,
    data
  );

const messagePath = (
  orgId: number | string,
  driverId: number | string,
  messageId: number
) => `${chatPath(orgId, driverId)}/messages/${messageId}`;

/** The author only: a colleague's message answers 400. Empty body = no caption. */
export const vdEditChatMessage = (
  orgId: number | string,
  driverId: number | string,
  messageId: number,
  body: string
) =>
  api.patch<ResponseType<VdChatMessage>>(
    messagePath(orgId, driverId, messageId),
    { body }
  );

/** Soft: the answer is the message with `deleted: true`. The author only. */
export const vdDeleteChatMessage = (
  orgId: number | string,
  driverId: number | string,
  messageId: number
) =>
  api.delete<ResponseType<VdChatMessage>>(
    messagePath(orgId, driverId, messageId)
  );

/** English to Spanish, anything else to English. 503 when it is off. */
export const vdTranslateChatMessage = (
  orgId: number | string,
  driverId: number | string,
  messageId: number
) =>
  api.post<ResponseType<VdChatTranslation>>(
    `${messagePath(orgId, driverId, messageId)}/translate`
  );

/** Text and captions, case-insensitive, newest first; pages like the list. */
export const vdSearchChatMessages = (
  orgId: number | string,
  driverId: number | string,
  params: { query: string; limit?: number; beforeId?: number }
) =>
  api.get<ResponseType<VdChatSearchResponse>>(
    `${chatPath(orgId, driverId)}/messages/search`,
    { params }
  );

/**
 * One file per call. PUT it to `uploadUrl` with exactly `headers`, then send
 * the `attachmentId`; files show in the order they were presigned.
 */
export const vdPresignChatAttachment = (
  orgId: number | string,
  driverId: number | string,
  data: VdPresignChatAttachmentDto
) =>
  api.post<ResponseType<VdPresignChatAttachmentResponse>>(
    `${chatPath(orgId, driverId)}/attachments/presign`,
    data
  );

export const vdReadChat = (orgId: number | string, driverId: number | string) =>
  api.post<ResponseType<VdChatUnreadCount>>(
    `${chatPath(orgId, driverId)}/read`
  );
